import CompareChangesPopup, { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import RectangleButton from 'particles/Button/RectangleButton'
import React, { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectCampaignByTemplateData } from 'state'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { TemplateDetailSchema } from './TemplateDetail'
import { useFormikContext } from 'formik'
import { TemplateFilterSchema } from './TemplateSelector'
import _ from 'lodash'
import { CampaignTaskElements } from 'services/api/campaignTemplates'

export interface SaveChangesProps {
    onSave: (onSuccessCallback: VoidFunction) => void
    checkForLiveEditBriefAndDeadline?: boolean
}

// This is component will be used for save changes function for live campaign and will be rendered at template-save-button-portal
// This handles and show compare modal when user tries to move away on the current screen
const SaveChangesWrapper: React.FC<SaveChangesProps> = ({ onSave: baseOnSave, checkForLiveEditBriefAndDeadline }) => {
    const [t] = useTranslation()
    const { values, setValues, resetForm } = useFormikContext<TemplateDetailSchema & TemplateFilterSchema>()
    const [initialValues, setInitialValues] = useState(values)
    const saveButtonEl = document.getElementById('template-save-button-portal')

    const onResetChanges = () => {
        resetForm()
        setValues(initialValues)
    }

    const onSave = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        baseOnSave(() => {
            setInitialValues(values)
        })
    }

    /**
     * This is to avoid triggering the CompareChangesPopup even though the array was not filled
     * All of our task element values that is using <FormikTextListGroup isDefault/>
     * is triggering a setValue function that auto add one input with is blank
     * {uuid:'someUuid', value: ''}
     * This function will remove this empty value to do the correct comparison
     */
    const getCleanValues = (valuesToClean: TemplateDetailSchema & TemplateFilterSchema) => {
        const result: TemplateDetailSchema & TemplateFilterSchema = JSON.parse(JSON.stringify(valuesToClean))
        const cleanedTaskElements = result.task_elements?.map(element => {
            // Iterate over each key-value pair (array) in the element
            const cleanedElement: Partial<CampaignTaskElements> = {}
            Object.entries(element).forEach(([key, value]) => {
                // Note: not all array from input parameter is an object with `uuid` & `value`, some array is string array
                // so, using hasOwnProperty to prevent TypeError while trying to access property uuid & value of a string
                if (
                    Array.isArray(value) &&
                    // hasOwnProperty did not check the prototype inheritance. if need to check inherited properties, please use different method here
                    value.every(item => item?.hasOwnProperty('uuid') && item?.hasOwnProperty('value'))
                ) {
                    // Filter out any objects with empty 'value'
                    cleanedElement[key as keyof CampaignTaskElements] = value.filter(item => item.value !== '') as any
                } else {
                    cleanedElement[key as keyof CampaignTaskElements] = value
                }
            })

            return cleanedElement
        })

        return { ...result, task_elements: cleanedTaskElements }
    }

    const cleanInitialValues = useMemo(() => getCleanValues(initialValues), [initialValues])
    const cleanValues = useMemo(() => getCleanValues(values), [values])

    return (
        <>
            {saveButtonEl &&
                createPortal(
                    <EditTemplateRestricted
                        checkForLiveEditBriefAndDeadline={checkForLiveEditBriefAndDeadline}
                        statusesToShow={[CAMPAIGN_STATUS_ENUM.LIVE]}
                    >
                        <RectangleButton
                            form="filled"
                            data-testid="test-campaign-save"
                            size="m"
                            label={t('button.saveChanges')}
                            onClick={onSave}
                        />
                    </EditTemplateRestricted>,
                    saveButtonEl,
                )}
            <CompareChangesPopup
                initialData={cleanInitialValues}
                compareData={cleanValues}
                recordChangesKey={RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE}
                onConfirm={onResetChanges}
            />
        </>
    )
}

const SaveChanges: React.FC<SaveChangesProps> = props => {
    const campaign = useSelector(selectCampaignByTemplateData)
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE
    return isCampaignLive ? <SaveChangesWrapper {...props} /> : null
}

export default SaveChanges
